// inspections.config.js
import React from "react";
import { FormattedDate, FormattedMessage } from "react-intl";
import { LOCALSTORAGE_ITEMS, TYPES } from "../../util/Constants";
import { PAGE_SIZE } from "../../config/ClientConfig";
import * as StorageService from "../../services/StorageServices";
import { PATH } from "../../routes/routes";
import StatusBadge from "../../components/StatusBadge";
import LocationInspectionDetails from "./LocationInspectionDetails";
import {
  getAllLocationInspectionFormsHandler,
  getMyLocationInspectionFormsHandler,
  deleteLocationInspectionFormByIdHandler,
} from "../../handlers/locationInspectionHandlers/HandlerLocationInspectionForms";

export const INSPECTIONS_DOC_TYPE = "inspections";

const DELETE_STATUS_NOT_ALLOWED_ERROR_CODE = 3;

const employeeName = (employee) =>
  employee ? `${employee.firstName || ""} ${employee.lastName || ""}`.trim() : "";

function DateCell({ value }) {
  if (!value) return "-";
  return (
    <FormattedDate value={value} day="numeric" month="short" year="numeric" hour="numeric" minute="numeric" hour12={false} />
  );
}

export const inspectionsConfig = ({ companyId }) => {
  const type = StorageService.getItem(LOCALSTORAGE_ITEMS.TYPE) || "Unknown";
  const canManage = type === TYPES.ADMIN || type === TYPES.MANAGER || type === TYPES.SUPER_ADMIN;

  const columnsBase = [
    {
      Header: "#",
      accessor: "rowNo",
      isSortable: false,
      disableFilters: true,
      filterable: false,
      Cell: (row) => row.value,
    },
    {
      Header: <FormattedMessage id="Inspector" defaultMessage="Inspector" />,
      accessor: "inspectorName",
      isSortable: false,
    },
    {
      Header: <FormattedMessage id="Location" defaultMessage="Location" />,
      accessor: "locationName",
      isSortable: false,
    },
    {
      Header: <FormattedMessage id="Operator" defaultMessage="Operator" />,
      accessor: "operatorName",
      isSortable: false,
    },
    {
      Header: <FormattedMessage id="InspectionDate" defaultMessage="InspectionDate" />,
      accessor: "inspectionDate",
      isSortable: true,
      disableFilters: true,
      Cell: (row) => <DateCell value={row.value} />,
    },
    {
      Header: <FormattedMessage id="Status" defaultMessage="Status" />,
      accessor: "status",
      isSortable: false,
      Cell: (row) => <StatusBadge status={row.value} />,
    },
  ];

  // Header must stay the literal string "Actions" - Table.js matches it by string
  // to decide whether to render the delete link.
  const columns = canManage
    ? [...columnsBase, { Header: "Actions", accessor: "actions", isSortable: false, filterable: false }]
    : columnsBase;

  return {
    path: PATH.INSPECTIONS(companyId),
    label: <FormattedMessage id="Inspections" defaultMessage="Inspections" />,
    value: INSPECTIONS_DOC_TYPE,
    headerTitle: <FormattedMessage id="Inspections" defaultMessage="Inspections" />,
    addBtnText: <FormattedMessage id="Add" defaultMessage="Add" />,
    pageSize: PAGE_SIZE,
    canAdd: canManage,
    searchBoxPlaceHolder: "Inspector, operator",
    DetailsComponent: LocationInspectionDetails,
    sortField: "inspectionDate",

    // createdFrom/createdTo are the generic date range coming from the pickers;
    // for inspections it applies to the inspection date, not the creation date.
    getAll: async ({ createdFrom, createdTo, page = 0, sort, locationsIds } = {}) => {
      const params = { page, size: PAGE_SIZE, sort: sort || ["inspectionDate,DESC"] };
      if (createdFrom) params.inspectionDateFrom = createdFrom;
      if (createdTo) params.inspectionDateTo = createdTo;
      if (locationsIds && locationsIds.length) params.locationsIds = locationsIds;

      if (type === TYPES.WORKER) {
        return getMyLocationInspectionFormsHandler(params);
      }

      return getAllLocationInspectionFormsHandler({ ...params, companyId });
    },

    del: async (id) => {
      const resp = await deleteLocationInspectionFormByIdHandler(id);

      // errorCode 3 = the backend refuses to delete a finalized/cancelled form.
      // alertTitle makes the table show it in a dialog instead of a browser alert.
      if (resp?.data?.errorCode === DELETE_STATUS_NOT_ALLOWED_ERROR_CODE) {
        return {
          data: {
            ...resp.data,
            alertTitle: "Ștergere nepermisă",
            message: "Doar fișele în stare Ciornă sau În lucru pot fi șterse.",
          },
        };
      }

      return resp;
    },

    columns,

    pickListFromResponse: (data) => data?.items || [],

    mapList: (items) =>
      (items || []).map((form, idx) => ({
        ...form,
        rowNo: idx + 1,
        inspectorName: employeeName(form?.inspector) || "-",
        operatorName: employeeName(form?.operator) || "-",
        locationName: form?.location?.name || "-",
        locationId: String(form?.location?.id || ""),
        inspectionDate: form?.inspectionDate || null,
        status: (form?.status || "DRAFT").toUpperCase(),
      })),

    deleteAlertTitle: "Ștergere fișă de verificare",
    deleteAlertText: "Ești sigur că vrei să ștergi fișa de verificare?",
    getDeleteLabel: (row) => `${row?.locationName || "-"} / ${row?.operatorName || "-"}`,
  };
};
